import React, { useState, useEffect, useCallback } from 'react';
import {
  CircularProgress,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Collapse,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material';
import Fluxo from '../Fluxograma/Fluxo';
import KanbanCard from './KanbanCard';
import './Kanban.css';
import { getFluxogramas, getGenericData, getUsuarios, getFornecedores, getOss, createOs } from '../../api';
import config from '../../../config.json';
import ModalLoading from '../../ModalLoading';

const ID_FLUXO_OS = 50;
const ID_FLUXO_GARANTIA = 51;

const dataAtual = new Date();
const primeiroDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth()-2, 1).toISOString().split('T')[0];
const ultimoDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth() + 1, 0).toISOString().split('T')[0];

const getInitialFilters = () => ({
  unidades: [],
  classificacoes: [],
  responsaveis: [],
  fornecedores: [],
  distribuidoras: [],
  dataInicio: primeiroDiaDoMes,
  dataFim: ultimoDiaDoMes,
});

function Kanban() {
  const [loading, setLoading] = useState(true);
  const [fluxos, setFluxos] = useState([]);
  const [isGarantia, setIsGarantia] = useState(false);
  const [currentEtapas, setCurrentEtapas] = useState([]);
  const [ossPorEtapa, setOssPorEtapa] = useState({});
  const [ossAtrasadas, setOssAtrasadas] = useState([]);
  const [ossCanceladas, setOssCanceladas] = useState([]);
  const [ossReprovadas, setOssReprovadas] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Estado dos filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    unidades: [],
    classificacoes: [],
    responsaveis: [],
    fornecedores: [],
    distribuidoras: [],
  });
  const [activeFilters, setActiveFilters] = useState(getInitialFilters());

  // Estado do Modal
  const [isFluxoOpen, setIsFluxoOpen] = useState(false);
  const [selectedOs, setSelectedOs] = useState(null);

  // Carrega os fluxos e as opções dos filtros na montagem
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [allFluxos, classificacoes, usuarios, fornecedores, distribuidoras] = await Promise.all([
          getFluxogramas(),
          getGenericData('classificacao'),
          getUsuarios(),
          getFornecedores(),
          getGenericData('distribuidora'),
        ]);

        setFluxos(allFluxos);
        setFilterOptions({
          unidades: config.unidades,
          classificacoes,
          responsaveis: usuarios.filter((u) => u.ativo), // Apenas ativos
          fornecedores,
          distribuidoras,
        });
      } catch (error) {
        console.error('Erro ao carregar dados iniciais do Kanban:', error);
        alert('Erro ao carregar dados do Kanban.');
      }
    };
    loadInitialData();
  }, []);

  // Busca os dados do Kanban sempre que o fluxo ou os filtros mudarem
  const fetchKanbanData = useCallback(async () => {
    setLoading(true);

    const currentFluxoId = isGarantia ? ID_FLUXO_GARANTIA : ID_FLUXO_OS;
    const fluxoAtual = fluxos.find((f) => f.id === currentFluxoId);

    if (!fluxoAtual) {
      if (fluxos.length > 0) console.error(`Fluxo com ID ${currentFluxoId} não encontrado.`);
      setLoading(false);
      return;
    }

    setCurrentEtapas(fluxoAtual.etapas);

    // 1. Prepara a estrutura de colunas vazias
    const etapasMap = {};
    // 2. CORRIGIDO: Troca de 'for...of' para '.forEach'
    fluxoAtual.etapas.forEach((etapa) => {
      etapasMap[etapa.id] = [];
    });

    // 2. Prepara o filtro para a API
    const filtroApi = {
      ...activeFilters,
      fluxoId: currentFluxoId,
      status: ['Aberta', 'Finalizada'],
    };

    try {
      // 3. Busca as OSs
      const fetchedOss = await getOss(filtroApi);
      const fetchedOssAtrasadas = await getOss({ ...filtroApi, status: ['Atrasada'] });
      const fetchedOssCanceladas = await getOss({ ...filtroApi, status: ['Cancelada'] });
      const fetchedOssReprovadas = await getOss({ ...filtroApi, status: ['Reprovada'] });

      // 4. Organiza as OSs nas colunas
      // 3. CORRIGIDO: Troca de 'for...of' para '.forEach'
      fetchedOss.forEach((os) => {
        // 4. CORRIGIDO: Troca de '.hasOwnProperty' para 'Object.prototype.hasOwnProperty.call'
        if (os.etapa && Object.prototype.hasOwnProperty.call(etapasMap, os.etapa.id)) {
          etapasMap[os.etapa.id].push(os);
        }
      });

      setOssPorEtapa(etapasMap);
      setOssAtrasadas(fetchedOssAtrasadas);
      setOssCanceladas(fetchedOssCanceladas);
      setOssReprovadas(fetchedOssReprovadas);
    } catch (error) {
      console.error('Erro ao buscar OSs:', error);
      alert('Erro ao carregar OSs.');
    } finally {
      setLoading(false);
    }
  }, [fluxos, isGarantia, activeFilters]);

  // Efeito principal para recarregar os dados
  useEffect(() => {
    if (fluxos.length > 0) {
      fetchKanbanData();
    }
  }, [fluxos, isGarantia, activeFilters, fetchKanbanData]);

  // Handlers de Filtro
  const handleMultiSelectChange = (filterKey, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterKey]: prev[filterKey].includes(value)
        ? prev[filterKey].filter((item) => item !== value) // Remove
        : [...prev[filterKey], value], // Adiciona
    }));
  };

  const handleDateChange = (e) => {
    setActiveFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleClearFilters = () => {
    setActiveFilters(getInitialFilters());
  };

  // Handlers do Modal
  const handleCardDoubleClick = (os) => {
    setSelectedOs(os);
    setIsFluxoOpen(true);
  };

  const handleCloseModal = () => {
    setIsFluxoOpen(false);
    setSelectedOs(null);
  };

  const onConfirmNewSale = async (data) => {
    setModalLoading(true);
    const responseOs = await createOs(data);
    if (!responseOs || !responseOs.success) {
      window.alert(responseOs?.message || 'Erro ao salvar a OS. Tente novamente.');
    }
    if (responseOs.warning) {
      window.alert(
        'Erro ao enviar a mensagem automática para o cliente, verifique a conexão do WhatsApp e o telefone do cliente'
      );
    }
    setModalLoading(false);
    fetchKanbanData();
    handleCloseModal();
  };

  const getFluxoNome = () => {
    const id = isGarantia ? ID_FLUXO_GARANTIA : ID_FLUXO_OS;
    return fluxos.find((f) => f.id === id)?.nome || 'Kanban';
  };

  const handleTipoChange = (event) => {
    setIsGarantia(event.target.value === 'true');
  };

  return (
    <div className="kanban-wrapper">
      <div className="kanban-header-bar">
        <h1>{getFluxoNome()}</h1>
        <div className="kanban-controls">
          <FormControl component="fieldset">
            {/* Você pode adicionar um título para o grupo */}
            <FormLabel component="legend">Fluxo</FormLabel>
            <RadioGroup
              row // Coloca os botões lado a lado
              aria-label="tipo-atendimento"
              name="tipo-atendimento-group"
              value={isGarantia} // Vincula ao seu state booleano existente
              onChange={handleTipoChange}
            >
              <FormControlLabel value={false} control={<Radio />} label="Normal" />
              <FormControlLabel value control={<Radio />} label="Garantia" />
            </RadioGroup>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => setShowFilters((prev) => !prev)}
            style={{ backgroundColor: '#17a2b8', color: 'white' }}
          >
            Filtro
          </Button>
        </div>
      </div>

      {/* PAINEL DE FILTROS COLAPSÁVEL */}
      <Collapse in={showFilters}>
        <div className="kanban-filter-panel">
          {/* 5. CORRIGIDO: Troca de <label> para <strong> para corrigir erros de acessibilidade */}
          {/* Unidades */}
          <div className="filter-group">
            <strong className="filter-group-label">Unidade</strong>
            <div className="checkbox-list">
              {filterOptions.unidades.map((opt) => (
                <FormControlLabel
                  key={opt.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={activeFilters.unidades.includes(opt.id)}
                      onChange={() => handleMultiSelectChange('unidades', opt.id)}
                    />
                  }
                  label={opt.nome}
                />
              ))}
            </div>
          </div>
          {/* Classificacao */}
          <div className="filter-group">
            <strong className="filter-group-label">Classificação</strong>
            <div className="checkbox-list">
              {filterOptions.classificacoes.map((opt) => (
                <FormControlLabel
                  key={opt._id}
                  control={
                    <Checkbox
                      size="small"
                      checked={activeFilters.classificacoes.includes(opt._id)}
                      onChange={() => handleMultiSelectChange('classificacoes', opt._id)}
                    />
                  }
                  label={opt.nome}
                />
              ))}
            </div>
          </div>
          {/* Responsável */}
          <div className="filter-group">
            <strong className="filter-group-label">Responsável</strong>
            <div className="checkbox-list">
              {filterOptions.responsaveis.map((opt) => (
                <FormControlLabel
                  key={opt._id}
                  control={
                    <Checkbox
                      size="small"
                      checked={activeFilters.responsaveis.includes(opt._id)}
                      onChange={() => handleMultiSelectChange('responsaveis', opt._id)}
                    />
                  }
                  label={opt.nome}
                />
              ))}
            </div>
          </div>
          {/* Fornecedor */}
          <div className="filter-group">
            <strong className="filter-group-label">Fornecedor</strong>
            <div className="checkbox-list">
              {filterOptions.fornecedores.map((opt) => (
                <FormControlLabel
                  key={opt._id}
                  control={
                    <Checkbox
                      size="small"
                      checked={activeFilters.fornecedores.includes(opt._id)}
                      onChange={() => handleMultiSelectChange('fornecedores', opt._id)}
                    />
                  }
                  label={opt.nome}
                />
              ))}
            </div>
          </div>
          {/* Distribuidora */}
          <div className="filter-group">
            <strong className="filter-group-label">Distribuidora</strong>
            <div className="checkbox-list">
              {filterOptions.distribuidoras.map((opt) => (
                <FormControlLabel
                  key={opt._id}
                  control={
                    <Checkbox
                      size="small"
                      checked={activeFilters.distribuidoras.includes(opt._id)}
                      onChange={() => handleMultiSelectChange('distribuidoras', opt._id)}
                    />
                  }
                  label={opt.nome}
                />
              ))}
            </div>
          </div>
          {/* Datas */}
          <div className="filter-group">
            <strong className="filter-group-label">Período</strong>
            <TextField
              name="dataInicio"
              type="date"
              value={activeFilters.dataInicio}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              style={{ marginRight: 10 }}
            />
            <TextField
              name="dataFim"
              type="date"
              value={activeFilters.dataFim}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="filter-actions">
            <Button variant="outlined" color="secondary" onClick={handleClearFilters}>
              Limpar Filtros
            </Button>
            {/* Os filtros já aplicam automaticamente via useEffect, mas um botão 'Aplicar' poderia ser adicionado se você remover 'activeFilters' da dependência do useEffect e chamar fetchKanbanData() manualmente */}
          </div>
        </div>
      </Collapse>

      {/* QUADRO KANBAN */}
      <div className="kanban-board-scroll-container">
        {loading ? (
          <CircularProgress style={{ margin: 'auto' }} />
        ) : (
          <div
            className="kanban-board"
            style={{
              gridTemplateColumns: `repeat(${currentEtapas.length}, 280px)`,
            }}
          >
            {/* Colunas */}
            {currentEtapas.map((etapa) => (
              <div key={etapa.id} className="kanban-column">
                <div className="kanban-column-header" style={{ borderTopColor: etapa.color || '#ccc' }}>
                  <h3>{etapa.nome}</h3>
                  <span>({ossPorEtapa[etapa.id]?.length || 0})</span>
                </div>
                <div className="kanban-column-content">
                  {/* Cards */}
                  {ossPorEtapa[etapa.id] &&
                    ossPorEtapa[etapa.id].map((os) => (
                      <KanbanCard key={os._id} os={os} onDoubleClick={handleCardDoubleClick} />
                    ))}
                </div>
              </div>
            ))}
            <div key={999} className="kanban-column">
              <div className="kanban-column-header" style={{ borderTopColor: '#b40808ff' }}>
                <h3>Atrasado</h3>
                <span>({ossAtrasadas?.length || 0})</span>
              </div>
              <div className="kanban-column-content">
                {/* Cards */}
                {ossAtrasadas?.length > 0 &&
                  ossAtrasadas.map((os) => <KanbanCard key={os._id} os={os} onDoubleClick={handleCardDoubleClick} />)}
              </div>
            </div>
            <div key={998} className="kanban-column">
              <div className="kanban-column-header" style={{ borderTopColor: '#b40808ff' }}>
                <h3>Cancelado</h3>
                <span>({ossCanceladas?.length || 0})</span>
              </div>
              <div className="kanban-column-content">
                {/* Cards */}
                {ossCanceladas?.length > 0 &&
                  ossCanceladas.map((os) => <KanbanCard key={os._id} os={os} onDoubleClick={handleCardDoubleClick} />)}
              </div>
            </div>
            {isGarantia && (
              <div key={997} className="kanban-column">
                <div className="kanban-column-header" style={{ borderTopColor: '#b40808ff' }}>
                  <h3>Garantia Reprovada</h3>
                  <span>({ossReprovadas?.length || 0})</span>
                </div>
                <div className="kanban-column-content">
                  {/* Cards */}
                  {ossReprovadas?.length > 0 &&
                    ossReprovadas.map((os) => (
                      <KanbanCard key={os._id} os={os} onDoubleClick={handleCardDoubleClick} />
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODAL */}
      {isFluxoOpen && (
        <Fluxo open={isFluxoOpen} proposta={selectedOs} onClose={handleCloseModal} onConfirm={onConfirmNewSale} />
      )}
      {modalLoading && <ModalLoading open={modalLoading} />}
    </div>
  );
}

// 6. Removida a linha 'require' duplicada no final

export default Kanban;
